/**
 * Sentry integration for ranerzzz.
 *
 * Activate by setting `VITE_SENTRY_DSN` in the deploy environment (Lovable / Vercel).
 * Without the DSN, all functions are no-ops and the app falls back to console.
 *
 * See SENTRY_SETUP.md at the repo root for the full setup guide.
 */
import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

let initialized = false;

export const initSentry = () => {
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("[Sentry] VITE_SENTRY_DSN not set — error tracking disabled");
    return;
  }

  if (initialized) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance traces — sample 10 % of transactions to keep quota low.
    tracesSampleRate: 0.1,
    // Don't record session replays by default…
    replaysSessionSampleRate: 0.0,
    // …but always record when there's an error so we can see what happened.
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      const msg =
        event.message ||
        event.exception?.values?.[0]?.value ||
        "";

      // In-app browsers (Instagram, Facebook, Line, TikTok, Snapchat) inyectan
      // JS de tracking propio que rompe asumiendo webkit.messageHandlers,
      // iframes, beforeunload handlers, autofill, etc. Esos errores son del
      // WebView, no nuestros, y no podemos arreglarlos. Filtro multicapa:
      //   1) Por browser.name (cubre el caso típico).
      //   2) Por URL del user-agent string del request.
      //   3) Por stacktrace: si todos los frames vienen de iabjs:// o de
      //      scripts inyectados (sendDataToNative, autofill_*), drop.
      const browserName = (event.contexts as { browser?: { name?: string } } | undefined)
        ?.browser?.name;
      if (browserName && /^(Instagram|Facebook|Line|TikTok|Pinterest|Snapchat|FBAV|FBAN)/i.test(browserName)) {
        return null;
      }
      const userAgent =
        (event.request?.headers as Record<string, string> | undefined)?.["User-Agent"] ??
        (event.contexts?.runtime as { name?: string } | undefined)?.name ??
        "";
      if (typeof userAgent === "string" && /\b(Instagram|FB[AV]N|FBIOS|Line\/|TikTok|Pinterest|Snapchat)\b/i.test(userAgent)) {
        return null;
      }
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const inAppBrowserSignatures = [
        "iabjs://",                          // Instagram / FB in-app browser injected scripts
        "sendDataToNative",                   // Instagram bridge to native
        "sendPageHideMessage",                // Instagram bridge
        "setContactAutofillValuesFromBridge", // FB/Instagram autofill
        "execute_autofill_callback",          // FB/Instagram autofill
        "autofill_contact_enhanced",          // FB/Instagram autofill
        "webkit.messageHandlers",             // iOS WebView bridge
      ];
      const frameHasInAppSig = (f: { filename?: string; function?: string }) => {
        const fn = (f.filename ?? "") + " " + (f.function ?? "");
        return inAppBrowserSignatures.some((sig) => fn.includes(sig));
      };
      // Si TODO el stacktrace viene de un in-app browser, drop.
      if (frames.length > 0 && frames.every(frameHasInAppSig)) return null;
      // O si CUALQUIER frame menciona el bridge + el mensaje tiene firma clara
      // (autofill 'value', webkit messageHandlers, etc), también drop.
      if (frames.some(frameHasInAppSig) && typeof msg === "string" && (
        msg.includes("messageHandlers") ||
        msg.includes("autofill") ||
        msg.includes("reading 'value'") // típico del autofill bridge cuando le falta un field
      )) {
        return null;
      }

      // Browser noise that doesn't represent a real bug.
      if (typeof msg === "string" && msg.includes("ResizeObserver loop")) {
        return null;
      }

      // Network noise del cliente (offline, ad-block, request abortado).
      // No accionable — real outages se ven por volumen/uptime.
      const networkNoise = [
        "Failed to fetch", // Chrome/Firefox
        "Load failed", // Safari iOS — fetch abortado por red móvil
        "NetworkError when attempting to fetch resource", // Firefox
        "cancelled", // mobile fetch interrupted
      ];
      if (typeof msg === "string" && networkNoise.some((n) => msg.includes(n))) {
        return null;
      }

      // AbortError: pasa cuando un effect de React limpia un fetch en flight
      // (user navega antes de que termine) o cuando supabase auth-js libera
      // su lock. Es comportamiento esperado, no un bug.
      if (
        typeof msg === "string" &&
        (msg.includes("AbortError") || msg.includes("signal is aborted"))
      ) {
        return null;
      }

      // Chunks viejos post-deploy: cuando hay un nuevo build, los chunks con
      // hash anterior quedan inaccesibles. Cualquier user con la app abierta
      // ve este error hasta que refresque. No es accionable.
      const staleChunkPatterns = [
        "Importing a module script failed",
        "Failed to fetch dynamically imported module",
        "ChunkLoadError",
        "Loading chunk",
      ];
      if (typeof msg === "string" && staleChunkPatterns.some((p) => msg.includes(p))) {
        return null;
      }

      // Android WebView: cuando el contexto Java subyacente se libera mientras
      // el WebView todavía tiene referencias JS activas. No es código nuestro.
      if (typeof msg === "string" && msg.includes("Java object is gone")) {
        return null;
      }

      // vaul drawer: bug conocido de la librería en iOS cuando event.target
      // queda null tras un pointer out durante el desmontaje del drawer.
      // Cualquier traza con vaul/dist/index.mjs + null target/onRelease/onPointerOut
      // es la misma firma del bug (#1473, #1402, #1189 dentro de vaul).
      const vaulInStack = frames.some((f) =>
        (f.filename ?? "").includes("vaul/dist/index"),
      );
      if (
        vaulInStack &&
        typeof msg === "string" &&
        (msg.includes("'l.target'") ||
          msg.includes("null is not an object") ||
          msg.includes("Cannot read properties of null"))
      ) {
        return null;
      }

      return event;
    },
  });

  initialized = true;
};

type UserContext = {
  id: string;
  email?: string | null;
  full_name?: string | null;
} | null;

export const setUserContext = (user: UserContext) => {
  if (!dsn || !initialized) return;
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email ?? undefined,
      username: user.full_name ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }
};

// Postgrest / fetch errors arrive as plain objects ({ code, details, hint, message }).
// Sentry.captureException needs a real Error to produce a useful title and grouping.
const toError = (value: unknown): { error: Error; extra: Record<string, unknown> } => {
  if (value instanceof Error) return { error: value, extra: {} };

  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const message =
      typeof obj.message === "string" && obj.message.length > 0
        ? obj.message
        : `Non-Error captured: ${Object.keys(obj).join(", ")}`;
    const error = new Error(message);
    error.name = typeof obj.name === "string" ? obj.name : "CapturedObject";
    return { error, extra: { original: obj } };
  }

  return { error: new Error(String(value)), extra: { original: value } };
};

export const captureError = (
  error: unknown,
  context?: Record<string, unknown>,
) => {
  if (!dsn || !initialized) {
    // eslint-disable-next-line no-console
    console.error("[captureError]", error, context);
    return;
  }
  const { error: normalized, extra } = toError(error);
  const merged = { ...extra, ...(context ?? {}) };
  Sentry.captureException(
    normalized,
    Object.keys(merged).length > 0 ? { extra: merged } : undefined,
  );
};

export const captureMessage = (
  message: string,
  context?: Record<string, unknown>,
) => {
  if (!dsn || !initialized) {
    // eslint-disable-next-line no-console
    console.warn("[captureMessage]", message, context);
    return;
  }
  Sentry.captureMessage(message, context ? { extra: context } : undefined);
};

// Re-export Sentry so consumers (e.g. ErrorBoundary fallback) can use it.
export { Sentry };
